import { track } from './index';
import type { AnalyticsEvent, DealStage, DocFormat, JobOutcome } from './events';

const SAFE_KEY_PATTERN = /[^a-z0-9_-]+/g;

export function recordLetsTalkSession(action: 'started' | 'ended' | 'failed'): void {
  track({ name: 'lets_talk_session', properties: { action } });
}

export function recordLetsTalkRetrieval(
  outcome: 'answered' | 'no_evidence' | 'unavailable', citationCount: number,
): void {
  track({ name: 'lets_talk_retrieval_completed', properties: {
    outcome, citation_count: sanitizeCount(citationCount),
  } });
}

type PreferencesLike = Record<string, unknown> | null | undefined;

const PROJECT_TAB_FEATURES: Record<string, string> = {
  overview: 'project_overview',
  documents: 'documents',
  chat: 'chat',
  'analyst-workspace': 'analyst_workspace',
  voice: 'voice',
  legal: 'legal',
  cim: 'cim_analysis',
  'financial-model': 'financial_model',
  'sector-research': 'sector_research',
  'transaction-search': 'transaction_search',
  presentations: 'presentations',
  benchmarks: 'benchmarks',
  'ic-committee': 'ic_committee',
  ddq: 'ddq',
  'due-diligence': 'due_diligence',
  'ic-memo': 'ic_memo',
  'ic-presentation': 'ic_presentation',
  'model-generator': 'model_generator',
  infographics: 'infographics',
  activity: 'activity',
  questions: 'questions',
  'competitive-intel': 'competitive_intel',
  'customer-cube': 'customer_cube',
  'deal-comparison': 'deal_comparison',
  'excel-intelligence': 'excel_intelligence',
};

function sanitizeKey(value: string | null | undefined, fallback = 'unknown'): string {
  if (!value) return fallback;
  const normalized = value.toLowerCase().trim().replace(SAFE_KEY_PATTERN, '_').replace(/^_+|_+$/g, '');
  return normalized.slice(0, 80) || fallback;
}

export function recordICMemoPartnerReviewRequested(input: {
  projectId: string; operationId: string; reviewId?: string; outcome: 'success' | 'failed';
}): void {
  track({ name: 'memo_partner_review_requested', properties: {
    project_id: sanitizeOpaqueId(input.projectId), feature_area: 'ic_memo',
    operation_id: sanitizeOpaqueId(input.operationId),
    ...(input.reviewId ? { review_id: sanitizeOpaqueId(input.reviewId) } : {}),
    outcome: input.outcome,
  } });
}

function sanitizeOpaqueId(value: string | null | undefined): string {
  if (!value || !/^[a-zA-Z0-9_-]{6,128}$/.test(value)) return 'unknown';
  return value;
}

function sanitizeCount(value: number | null | undefined): number {
  if (!Number.isFinite(value ?? NaN)) return 0;
  return Math.max(0, Math.floor(value as number));
}

function sanitizeDurationMs(value: number | null | undefined): number {
  if (!Number.isFinite(value ?? NaN)) return 0;
  return Math.max(0, Math.min(Math.floor(value as number), 24 * 60 * 60 * 1000));
}

function queryLengthBucket(query: string): string {
  const length = query.trim().length;
  if (length === 0) return '0';
  if (length <= 20) return '1-20';
  if (length <= 80) return '21-80';
  if (length <= 200) return '81-200';
  return '201+';
}

function pageBucket(pageCount: number | null | undefined): string {
  const pages = sanitizeCount(pageCount);
  if (pages === 0) return 'unknown';
  if (pages <= 10) return '1-10';
  if (pages <= 50) return '11-50';
  if (pages <= 100) return '51-100';
  return '101+';
}

function toOutcome(value: string | null | undefined): JobOutcome {
  if (value === 'success' || value === 'partial' || value === 'failed' || value === 'timeout' || value === 'cancelled') {
    return value;
  }
  return value === 'complete' || value === 'completed' ? 'success' : 'failed';
}

function toDocFormat(value: string | null | undefined): DocFormat {
  const format = sanitizeKey(value).replace(/^\./, '');
  return format === 'pdf' || format === 'pptx' || format === 'docx' || format === 'xlsx' || format === 'csv'
    ? format
    : 'other';
}

function toDealStage(value: string | null | undefined): DealStage {
  switch (value) {
    case 'screening':
      return 'screening';
    case 'diligence':
      return 'diligence';
    case 'ic':
    case 'ic_prep':
    case 'ic_review':
      return 'ic_review';
    case 'closed':
    case 'portfolio_company':
      return 'portfolio';
    case 'exited':
      return 'exited';
    case 'passed':
    case 'dead':
      return 'passed';
    case 'prospect':
    case 'active_deal':
    case 'sourcing':
      return 'sourcing';
    case 'closing':
      return 'closing';
    default:
      return 'screening';
  }
}

function routePattern(pathname: string): string {
  return pathname
    .replace(/^\/project\/[^/?#]+/, '/project/:projectId')
    .replace(/^\/projects\/[^/?#]+\/cim\/[^/?#]+/, '/projects/:projectId/cim/:analysisId')
    .replace(/^\/legal\/create\/[^/?#]+\/[^/?#]+/, '/legal/create/:templateId/:projectId')
    .replace(/^\/legal\/create\/[^/?#]+/, '/legal/create/:templateId')
    .replace(/^\/legal\/edit\/[^/?#]+/, '/legal/edit/:documentId')
    .replace(/^\/legal\/view\/[^/?#]+/, '/legal/view/:documentId')
    .replace(/^\/legal\/generate\/[^/?#]+/, '/legal/generate/:projectId')
    .replace(/^\/portfolio\/[^/?#]+/, '/portfolio/:companyId')
    .replace(/^\/model-generator\/[^/?#]+/, '/model-generator/:projectId')
    .replace(/^\/blog\/[^/?#]+/, '/blog/:slug')
    .replace(/^\/solutions\/[^/?#]+/, '/solutions/:slug')
    .replace(/^\/compare\/[^/?#]+/, '/compare/:slug')
    .replace(/^\/resources\/[^/?#]+/, '/resources/:slug');
}

function featureFromLocation(pathname: string, search = ''): string | null {
  const params = new URLSearchParams(search);
  const tab = params.get('tab');
  if (pathname.startsWith('/project/') && tab) return PROJECT_TAB_FEATURES[tab] ?? null;
  if (pathname.startsWith('/project/')) return 'project_overview';
  if (pathname === '/dashboard') return 'dashboard';
  if (pathname.startsWith('/legal')) return 'legal';
  if (pathname.startsWith('/portfolio-monitor')) return 'portfolio_monitoring';
  if (pathname.startsWith('/portfolio')) return 'portfolio';
  if (pathname.startsWith('/documents')) return 'knowledge_base';
  if (pathname.startsWith('/model-generator')) return 'model_generator';
  if (pathname.startsWith('/deal-lifecycle')) return 'deal_lifecycle';
  if (pathname.includes('/cim/')) return 'cim_analysis';
  return null;
}

export function hasAnalyticsConsent(preferences: PreferencesLike): boolean {
  const privacyReviewed = import.meta.env.VITE_POSTHOG_PRIVACY_REVIEWED === 'true';
  if (!privacyReviewed || !preferences) return false;

  const privacy = preferences.privacy;
  const privacyConsent = typeof privacy === 'object' && privacy !== null
    ? (privacy as Record<string, unknown>).analyticsConsent
    : undefined;

  return preferences.analyticsConsent === true ||
    preferences.productAnalyticsConsent === true ||
    privacyConsent === true;
}

export function recordProductEvent(event: AnalyticsEvent): void {
  track(event);
}

export function recordChatGPTConsentCompleted(outcome: 'approved' | 'denied' | 'failed'): void {
  recordProductEvent({ name: 'chatgpt_consent_completed', properties: { outcome } });
}

export function recordResearchPublicationEvent(input: {
  event: 'requested' | 'completed' | 'downloaded' | 'failed' | 'canceled';
  projectId: string;
  publicationId: string;
  format: 'pdf' | 'pptx' | 'source_memo_pdf' | 'both';
}): void {
  recordProductEvent({
    name: `research_publication_${input.event}`,
    properties: {
      project_id: sanitizeOpaqueId(input.projectId),
      publication_id: sanitizeOpaqueId(input.publicationId),
      format: input.format,
    },
  } as AnalyticsEvent);
}

export function recordAppLoaded(authenticated: boolean): void {
  recordProductEvent({ name: 'app_loaded', properties: { authenticated } });
}

export function recordPageViewed(pathname: string): void {
  recordProductEvent({ name: 'page_viewed', properties: { route: routePattern(pathname) } });
}

export function recordFeatureOpened(pathname: string, search = ''): void {
  const feature = featureFromLocation(pathname, search);
  if (!feature) return;
  recordProductEvent({ name: 'feature_opened', properties: { feature } });
}

export function recordKnowledgeBaseLoadCompleted(input: {
  trigger: 'initial' | 'refresh' | 'delete' | 'load_more';
  outcome: 'success' | 'failed';
  documentCount: number;
  hasMore: boolean;
}): void {
  try {
    recordProductEvent({
      name: 'knowledge_base_load_completed',
      properties: {
        feature_area: 'knowledge_base',
        trigger: input.trigger,
        outcome: input.outcome,
        document_count: sanitizeCount(input.documentCount),
        has_more: input.hasMore === true,
      },
    });
  } catch {
    return;
  }
}

export function recordCIMUploaded(format: string, pageCount?: number): void {
  recordProductEvent({
    name: 'cim_uploaded',
    properties: {
      format: toDocFormat(format),
      page_bucket: pageBucket(pageCount),
    },
  });
}

export function recordCIMAnalysisCompleted(input: {
  projectId: string;
  outcome: JobOutcome | string;
  durationMs?: number;
  sectionsExtracted?: number;
}): void {
  recordProductEvent({
    name: 'cim_analysis_completed',
    properties: {
      project_id: sanitizeOpaqueId(input.projectId),
      outcome: toOutcome(input.outcome),
      duration_ms: sanitizeDurationMs(input.durationMs),
      sections_extracted: sanitizeCount(input.sectionsExtracted),
    },
  });
}

export function recordICMemoGenerationCompleted(input: {
  projectId: string;
  outcome: JobOutcome | string;
  durationMs?: number;
  continuationCount?: number;
}): void {
  recordProductEvent({
    name: 'ic_memo_generation_completed',
    properties: {
      project_id: sanitizeOpaqueId(input.projectId),
      outcome: toOutcome(input.outcome),
      duration_ms: sanitizeDurationMs(input.durationMs),
      continuation_count: sanitizeCount(input.continuationCount),
    },
  });
}

export function recordFinancialModelGenerationCompleted(input: {
  projectId: string;
  outcome: JobOutcome | string;
  durationMs?: number;
}): void {
  recordProductEvent({
    name: 'financial_model_generation_completed',
    properties: {
      project_id: sanitizeOpaqueId(input.projectId),
      outcome: toOutcome(input.outcome),
      duration_ms: sanitizeDurationMs(input.durationMs),
    },
  });
}

export function recordDealStageChanged(input: {
  projectId: string;
  fromStage?: string | null;
  toStage?: string | null;
}): void {
  recordProductEvent({
    name: 'deal_stage_changed',
    properties: {
      project_id: sanitizeOpaqueId(input.projectId),
      from_stage: toDealStage(input.fromStage),
      to_stage: toDealStage(input.toStage),
    },
  });
}

export function recordProjectEditCompleted(input: {
  projectId: string;
  fieldsUpdated: string[];
  outcome: JobOutcome | string;
}): void {
  recordProductEvent({
    name: 'project_edit_completed',
    properties: {
      project_id: sanitizeOpaqueId(input.projectId),
      fields_updated: input.fieldsUpdated.map((field) => sanitizeKey(field)).sort().join(','),
      outcome: toOutcome(input.outcome),
    },
  });
}

export function recordPortfolioDashboardViewed(companyCount: number): void {
  recordProductEvent({
    name: 'portfolio_dashboard_viewed',
    properties: {
      company_count: sanitizeCount(companyCount),
    },
  });
}

export function recordPortfolioExported(input: {
  exportFormat: 'xlsx' | 'csv' | 'pdf';
  outcome: JobOutcome | string;
}): void {
  recordProductEvent({
    name: 'portfolio_exported',
    properties: { export_format: input.exportFormat, outcome: toOutcome(input.outcome) },
  });
}

export function recordSearchPerformed(input: {
  searchMode: 'rag' | 'web';
  query: string;
  resultCount?: number;
}): void {
  recordProductEvent({
    name: 'search_performed',
    properties: {
      search_mode: input.searchMode,
      query_length_bucket: queryLengthBucket(input.query),
      result_count: sanitizeCount(input.resultCount),
    },
  });
}

export function recordLegalDocGenerationCompleted(input: {
  projectId: string;
  docType: string;
  outcome: JobOutcome | string;
  durationMs?: number;
}): void {
  recordProductEvent({
    name: 'legal_doc_generation_completed',
    properties: {
      project_id: sanitizeOpaqueId(input.projectId),
      doc_type: sanitizeKey(input.docType),
      outcome: toOutcome(input.outcome),
      duration_ms: sanitizeDurationMs(input.durationMs),
    },
  });
}

type OnboardingEvent = Extract<AnalyticsEvent, { name: `onboarding_${string}` }>;

/** Setup-guide events. Counts are clamped; the other events carry only enums and section ids. */
export function recordOnboardingEvent(event: OnboardingEvent): void {
  if (event.name === 'onboarding_completed') {
    recordProductEvent({
      name: 'onboarding_completed',
      properties: {
        fieldsCaptured: sanitizeCount(event.properties.fieldsCaptured),
        sectionsCompleted: sanitizeCount(event.properties.sectionsCompleted),
      },
    });
    return;
  }
  recordProductEvent(event);
}
